import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import {
  BarChart3,
  Download,
  FileText,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Calendar,
  Rocket,
  Target,
  Filter,
  ChevronRight,
  AlertCircle,
  FileCheck,
  CalendarDays
} from 'lucide-react';

// Color palette for charts
const COLORS = ['#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'];
const STATUS_COLORS = {
  idea: '#94a3b8',
  drafting: '#f59e0b',
  in_review: '#8b5cf6',
  approved: '#22c55e',
  scheduled: '#3b82f6',
  published: '#06b6d4'
};

const TASK_STATUS_COLORS = {
  backlog: '#94a3b8',
  in_progress: '#3b82f6',
  in_review: '#8b5cf6',
  blocked: '#ef4444',
  completed: '#22c55e'
};

// Reusable KPI Card
const KPICard = ({ icon: Icon, label, value, trend, trendValue, bgColor = 'bg-blue-50', iconColor = 'text-blue-600' }) => (
  <Card className="mcc-card">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg ${bgColor}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={1.5} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trendValue}
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

// Risk Badge
const RiskBadge = ({ status }) => {
  const styles = {
    green: 'bg-emerald-100 text-emerald-700',
    yellow: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    normal: 'bg-slate-100 text-slate-700',
    warning: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700'
  };
  return (
    <Badge className={`${styles[status] || styles.normal} capitalize font-medium`}>
      {status}
    </Badge>
  );
};

// Date Range Filter
const DateRangeFilter = ({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-[140px]" data-testid="date-range-filter">
      <SelectValue placeholder="Date range" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="7d">Last 7 days</SelectItem>
      <SelectItem value="30d">Last 30 days</SelectItem>
      <SelectItem value="90d">Last Quarter</SelectItem>
      <SelectItem value="365d">Last Year</SelectItem>
    </SelectContent>
  </Select>
);

// Loading Skeleton
const ReportSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <Card key={i} className="mcc-card">
          <CardContent className="p-4">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-8 w-20 mt-3" />
            <Skeleton className="h-4 w-24 mt-1" />
          </CardContent>
        </Card>
      ))}
    </div>
    <Card className="mcc-card">
      <CardContent className="p-6">
        <Skeleton className="h-64 w-full" />
      </CardContent>
    </Card>
  </div>
);

export const ReportsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'executive');
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  
  // Report data states
  const [executiveData, setExecutiveData] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [workloadData, setWorkloadData] = useState(null);
  const [eventsData, setEventsData] = useState(null);
  const [approvalsData, setApprovalsData] = useState(null);
  const [requestsKPIData, setRequestsKPIData] = useState(null);

  // Fetch report data based on active tab
  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const params = `?date_range=${dateRange}`;
      
      switch (activeTab) {
        case 'executive':
          const execRes = await api.get(`/reports/executive-overview${params}`);
          setExecutiveData(execRes.data);
          break;
        case 'campaigns':
          const campRes = await api.get(`/reports/campaign-performance${params}`);
          setCampaignData(campRes.data);
          break;
        case 'content':
          const contRes = await api.get(`/reports/content-pipeline${params}`);
          setContentData(contRes.data);
          break;
        case 'workload':
          const workRes = await api.get(`/reports/workload${params}`);
          setWorkloadData(workRes.data);
          break;
        case 'events':
          const evtRes = await api.get(`/reports/events${params}`);
          setEventsData(evtRes.data);
          break;
        case 'approvals':
          const appRes = await api.get(`/reports/approvals${params}`);
          setApprovalsData(appRes.data);
          break;
        case 'requests':
          const reqRes = await api.get(`/reports/requests-kpi${params}`);
          setRequestsKPIData(reqRes.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  }, [api, activeTab, dateRange]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  // Export handlers
  const handleExportCSV = () => {
    // Implementation for CSV export
    console.log('Exporting CSV...');
  };

  const handleExportPDF = () => {
    // Implementation for PDF export
    console.log('Exporting PDF...');
  };

  // Drill-down handler
  const handleDrillDown = (type, filter) => {
    switch (type) {
      case 'tasks':
        navigate(`/tasks?status=${filter}`);
        break;
      case 'content':
        navigate(`/calendar?status=${filter}`);
        break;
      case 'projects':
        navigate(`/projects?status=${filter}`);
        break;
      case 'events':
        navigate(`/events`);
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Reports & Analytics</h1>
          <p className="text-slate-500 mt-1">Executive insights and operational metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1 h-auto flex-wrap">
          <TabsTrigger value="executive" className="data-[state=active]:bg-white" data-testid="tab-executive">
            <BarChart3 className="w-4 h-4 mr-2" />
            Executive Overview
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-white" data-testid="tab-campaigns">
            <Target className="w-4 h-4 mr-2" />
            Campaign Performance
          </TabsTrigger>
          <TabsTrigger value="content" className="data-[state=active]:bg-white" data-testid="tab-content">
            <Rocket className="w-4 h-4 mr-2" />
            Content Pipeline
          </TabsTrigger>
          <TabsTrigger value="workload" className="data-[state=active]:bg-white" data-testid="tab-workload">
            <Users className="w-4 h-4 mr-2" />
            Workload
          </TabsTrigger>
          <TabsTrigger value="events" className="data-[state=active]:bg-white" data-testid="tab-events">
            <CalendarDays className="w-4 h-4 mr-2" />
            Events
          </TabsTrigger>
          <TabsTrigger value="approvals" className="data-[state=active]:bg-white" data-testid="tab-approvals">
            <FileCheck className="w-4 h-4 mr-2" />
            Approvals
          </TabsTrigger>
          <TabsTrigger value="requests" className="data-[state=active]:bg-white" data-testid="tab-requests">
            <TrendingUp className="w-4 h-4 mr-2" />
            Request KPIs
          </TabsTrigger>
        </TabsList>

        {/* Executive Overview Tab */}
        <TabsContent value="executive" className="space-y-6">
          {loading ? <ReportSkeleton /> : executiveData && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div onClick={() => handleDrillDown('content', 'published')} className="cursor-pointer">
                  <KPICard
                    icon={Rocket}
                    label="Launches This Period"
                    value={executiveData.kpis.launches_this_period}
                    bgColor="bg-blue-50"
                    iconColor="text-blue-600"
                  />
                </div>
                <div onClick={() => handleDrillDown('tasks', 'overdue')} className="cursor-pointer">
                  <KPICard
                    icon={AlertTriangle}
                    label="Overdue Tasks"
                    value={executiveData.kpis.overdue_tasks}
                    bgColor="bg-amber-50"
                    iconColor="text-amber-600"
                  />
                </div>
                <div onClick={() => handleDrillDown('tasks', 'blocked')} className="cursor-pointer">
                  <KPICard
                    icon={AlertCircle}
                    label="Blocked Tasks"
                    value={executiveData.kpis.blocked_tasks}
                    bgColor="bg-red-50"
                    iconColor="text-red-600"
                  />
                </div>
                <div onClick={() => handleDrillDown('projects', 'at_risk')} className="cursor-pointer">
                  <KPICard
                    icon={Target}
                    label="Campaigns At Risk"
                    value={executiveData.kpis.campaigns_at_risk}
                    bgColor="bg-purple-50"
                    iconColor="text-purple-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard
                  icon={CalendarDays}
                  label="Events This Period"
                  value={executiveData.kpis.events_this_period}
                  bgColor="bg-cyan-50"
                  iconColor="text-cyan-600"
                />
                <KPICard
                  icon={FileCheck}
                  label="Approval Backlog"
                  value={executiveData.kpis.approval_backlog}
                  bgColor="bg-orange-50"
                  iconColor="text-orange-600"
                />
                <KPICard
                  icon={Clock}
                  label="Avg Task Completion"
                  value={`${executiveData.kpis.avg_task_completion_days}d`}
                  bgColor="bg-slate-50"
                  iconColor="text-slate-600"
                />
                <KPICard
                  icon={CheckCircle}
                  label="On-Time Launch %"
                  value={`${executiveData.kpis.on_time_launch_pct}%`}
                  bgColor="bg-emerald-50"
                  iconColor="text-emerald-600"
                />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Content Status */}
                <Card className="mcc-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-slate-900">Content Status Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 min-h-[256px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <PieChart>
                          <Pie
                            data={executiveData.charts.content_by_status}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={70}
                            paddingAngle={3}
                            dataKey="count"
                            nameKey="status"
                          >
                            {executiveData.charts.content_by_status.map((entry, index) => (
                              <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {executiveData.charts.content_by_status.map((item) => (
                        <div key={item.status} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[item.status] }} />
                          <span className="text-xs text-slate-600 capitalize">{item.status.replace('_', ' ')}</span>
                          <span className="text-xs font-semibold text-slate-900 ml-auto">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Tasks by Status */}
                <Card className="mcc-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-slate-900">Tasks by Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 min-h-[256px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <BarChart data={executiveData.charts.tasks_by_status} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis dataKey="status" type="category" width={80} tick={{ fontSize: 11 }} tickFormatter={(v) => v.replace('_', ' ')} />
                          <Tooltip />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                            {executiveData.charts.tasks_by_status.map((entry) => (
                              <Cell key={entry.status} fill={TASK_STATUS_COLORS[entry.status] || '#94a3b8'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Workload Distribution */}
                <Card className="mcc-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-slate-900">Team Workload</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 min-h-[256px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <BarChart data={executiveData.charts.workload_distribution} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="tasks" fill="#2563eb" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Metrics Summary */}
              <Card className="mcc-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Key Metrics Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-3xl font-bold text-slate-900">{executiveData.metrics.on_time_launch_rate}%</p>
                      <p className="text-sm text-slate-500 mt-1">On-Time Launch Rate</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-3xl font-bold text-slate-900">{executiveData.metrics.task_completion_rate}%</p>
                      <p className="text-sm text-slate-500 mt-1">Task Completion Rate</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-3xl font-bold text-slate-900">{executiveData.metrics.avg_days_to_approval}d</p>
                      <p className="text-sm text-slate-500 mt-1">Avg Days to Approval</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-3xl font-bold text-slate-900">{executiveData.metrics.blocked_task_pct}%</p>
                      <p className="text-sm text-slate-500 mt-1">Blocked Task %</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Campaign Performance Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          {loading ? <ReportSkeleton /> : campaignData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <KPICard icon={Target} label="Total Campaigns" value={campaignData.summary.total_campaigns} bgColor="bg-blue-50" iconColor="text-blue-600" />
                <KPICard icon={CheckCircle} label="Active" value={campaignData.summary.active} bgColor="bg-emerald-50" iconColor="text-emerald-600" />
                <KPICard icon={AlertTriangle} label="At Risk" value={campaignData.summary.at_risk} bgColor="bg-amber-50" iconColor="text-amber-600" />
                <KPICard icon={AlertCircle} label="Critical" value={campaignData.summary.critical} bgColor="bg-red-50" iconColor="text-red-600" />
                <KPICard icon={TrendingUp} label="Avg Completion" value={`${campaignData.summary.avg_completion}%`} bgColor="bg-purple-50" iconColor="text-purple-600" />
              </div>

              {/* Campaign Table */}
              <Card className="mcc-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Campaign Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Campaign</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Content</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Tasks</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Overdue</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Upcoming</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Events</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Risk</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaignData.campaigns.map((campaign) => (
                          <tr 
                            key={campaign.id} 
                            className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate(`/projects?id=${campaign.id}`)}
                          >
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-900">{campaign.name}</p>
                                <p className="text-xs text-slate-500">{campaign.start_date} - {campaign.end_date}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="outline" className="capitalize">{campaign.status}</Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-sm font-medium">{campaign.completed_content_pct}%</span>
                                <Progress value={campaign.completed_content_pct} className="w-16 h-2" />
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-sm font-medium">{campaign.tasks_completed_pct}%</span>
                                <Progress value={campaign.tasks_completed_pct} className="w-16 h-2" />
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-sm font-medium ${campaign.overdue_tasks > 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {campaign.overdue_tasks}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm font-medium text-slate-600">{campaign.upcoming_launches}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm font-medium text-slate-600">{campaign.events_count}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <RiskBadge status={campaign.risk_status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Content Pipeline Tab */}
        <TabsContent value="content" className="space-y-6">
          {loading ? <ReportSkeleton /> : contentData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <KPICard icon={FileText} label="Total Content" value={contentData.summary.total_content} bgColor="bg-slate-50" iconColor="text-slate-600" />
                <KPICard icon={Clock} label="In Production" value={contentData.summary.in_production} bgColor="bg-blue-50" iconColor="text-blue-600" />
                <KPICard icon={CheckCircle} label="Ready to Publish" value={contentData.summary.ready_to_publish} bgColor="bg-emerald-50" iconColor="text-emerald-600" />
                <KPICard icon={FileCheck} label="Awaiting Approval" value={contentData.summary.awaiting_approval} bgColor="bg-purple-50" iconColor="text-purple-600" />
                <KPICard icon={Calendar} label="Scheduled" value={contentData.summary.scheduled} bgColor="bg-cyan-50" iconColor="text-cyan-600" />
                <KPICard icon={Rocket} label="Published" value={contentData.summary.published} bgColor="bg-emerald-50" iconColor="text-emerald-600" />
                <KPICard icon={AlertTriangle} label="Overdue" value={contentData.summary.overdue} bgColor="bg-red-50" iconColor="text-red-600" />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Content by Status */}
                <Card className="mcc-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-slate-900">Content by Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 min-h-[256px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                        <BarChart data={contentData.charts.content_by_status}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="status" tick={{ fontSize: 11 }} tickFormatter={(v) => v.replace('_', ' ')} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                            {contentData.charts.content_by_status.map((entry) => (
                              <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#94a3b8'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Content by Channel */}
                <Card className="mcc-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-slate-900">Content by Channel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 min-h-[256px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                        <BarChart data={contentData.charts.content_by_channel} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis dataKey="channel" type="category" width={100} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* At Risk Items */}
              {contentData.at_risk_items.length > 0 && (
                <Card className="mcc-card border-amber-200">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <CardTitle className="text-base font-semibold text-slate-900">Launch Risk Items</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {contentData.at_risk_items.map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900">{item.title}</p>
                            <p className="text-xs text-slate-500">{item.risk_reason}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-amber-600">{item.publish_date}</p>
                            <Badge variant="outline" className="text-xs capitalize">{item.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Workload Tab */}
        <TabsContent value="workload" className="space-y-6">
          {loading ? <ReportSkeleton /> : workloadData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <KPICard icon={Users} label="Team Members" value={workloadData.summary.total_team_members} bgColor="bg-blue-50" iconColor="text-blue-600" />
                <KPICard icon={CheckCircle} label="Active Tasks" value={workloadData.summary.total_active_tasks} bgColor="bg-emerald-50" iconColor="text-emerald-600" />
                <KPICard icon={AlertTriangle} label="Total Overdue" value={workloadData.summary.total_overdue} bgColor="bg-amber-50" iconColor="text-amber-600" />
                <KPICard icon={AlertCircle} label="Total Blocked" value={workloadData.summary.total_blocked} bgColor="bg-red-50" iconColor="text-red-600" />
                <KPICard icon={TrendingUp} label="Avg Capacity" value={`${workloadData.summary.avg_team_capacity}%`} bgColor="bg-purple-50" iconColor="text-purple-600" />
                <KPICard icon={AlertCircle} label="Members At Risk" value={workloadData.summary.members_at_risk} bgColor="bg-orange-50" iconColor="text-orange-600" />
              </div>

              {/* Team Workload Table */}
              <Card className="mcc-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Team Workload Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Team Member</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Active</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Overdue</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Blocked</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Due This Week</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Est. Hours</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Capacity</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workloadData.team.map((member) => (
                          <tr key={member.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-900">{member.name}</p>
                                <p className="text-xs text-slate-500 capitalize">{member.role}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm font-medium text-slate-900">{member.active_tasks}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-sm font-medium ${member.overdue_tasks > 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {member.overdue_tasks}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-sm font-medium ${member.blocked_tasks > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                                {member.blocked_tasks}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm font-medium text-slate-600">{member.tasks_due_this_week}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm font-medium text-slate-600">{member.estimated_hours}h</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <span className={`text-sm font-medium ${
                                  member.capacity_pct > 100 ? 'text-red-600' : 
                                  member.capacity_pct > 85 ? 'text-amber-600' : 'text-slate-600'
                                }`}>
                                  {member.capacity_pct}%
                                </span>
                                <Progress 
                                  value={Math.min(member.capacity_pct, 100)} 
                                  className={`w-16 h-2 ${
                                    member.capacity_pct > 100 ? '[&>div]:bg-red-500' : 
                                    member.capacity_pct > 85 ? '[&>div]:bg-amber-500' : ''
                                  }`}
                                />
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <RiskBadge status={member.risk} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Overdue Trend Chart */}
              <Card className="mcc-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Overdue Tasks Trend (7 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64 min-h-[256px]">
                    <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                      <LineChart data={workloadData.charts.overdue_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-6">
          {loading ? <ReportSkeleton /> : eventsData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard icon={CalendarDays} label="Events This Period" value={eventsData.summary.events_this_period} bgColor="bg-blue-50" iconColor="text-blue-600" />
                <KPICard icon={FileText} label="Promo Content Created" value={eventsData.summary.promotion_content_created} bgColor="bg-purple-50" iconColor="text-purple-600" />
                <KPICard icon={CheckCircle} label="Task Completion %" value={`${eventsData.summary.task_completion_pct}%`} bgColor="bg-emerald-50" iconColor="text-emerald-600" />
                <KPICard icon={Clock} label="Upcoming Events" value={eventsData.summary.upcoming_events} bgColor="bg-cyan-50" iconColor="text-cyan-600" />
              </div>

              {/* Events Table */}
              <Card className="mcc-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Event Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Event</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Tasks</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Content</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Checklist</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Follow-up</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eventsData.events.map((event) => (
                          <tr 
                            key={event.id} 
                            className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate('/events')}
                          >
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-900">{event.name}</p>
                                <p className="text-xs text-slate-500">{event.location}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="outline" className="text-xs">{event.type}</Badge>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="outline" className="capitalize text-xs">{event.status}</Badge>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-slate-600">{event.start_date}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-sm font-medium">{event.tasks_completed_pct}%</span>
                                <Progress value={event.tasks_completed_pct} className="w-12 h-2" />
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm font-medium text-slate-600">{event.content_pieces}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm text-slate-600">{event.checklist_completed}/{event.checklist_total}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {event.followup_completed === null ? (
                                <span className="text-slate-400">-</span>
                              ) : event.followup_completed ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600 mx-auto" />
                              ) : (
                                <Clock className="w-4 h-4 text-amber-600 mx-auto" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Approvals Tab */}
        <TabsContent value="approvals" className="space-y-6">
          {loading ? <ReportSkeleton /> : approvalsData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <KPICard icon={FileCheck} label="Pending Approvals" value={approvalsData.summary.pending_approvals} bgColor="bg-amber-50" iconColor="text-amber-600" />
                <KPICard icon={Clock} label="Avg Days Waiting" value={approvalsData.summary.avg_days_waiting} bgColor="bg-blue-50" iconColor="text-blue-600" />
                <KPICard icon={AlertTriangle} label="Longest Pending" value={`${approvalsData.summary.longest_pending_days}d`} bgColor="bg-red-50" iconColor="text-red-600" />
                <KPICard icon={TrendingDown} label="Rejection Rate" value={`${approvalsData.summary.rejection_rate_pct}%`} bgColor="bg-purple-50" iconColor="text-purple-600" />
                <KPICard icon={AlertCircle} label="Flagged Items" value={approvalsData.summary.flagged_items} bgColor="bg-orange-50" iconColor="text-orange-600" />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Time Distribution */}
                <Card className="mcc-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-slate-900">Approval Time Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 min-h-[256px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <BarChart data={approvalsData.charts.time_distribution}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Bottleneck by Person */}
                <Card className="mcc-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-slate-900">Approval Bottleneck by Person</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 min-h-[256px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <BarChart data={approvalsData.charts.bottleneck_by_person} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="pending" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Pending Items Table */}
              <Card className="mcc-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Pending Approvals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Content</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Approver</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Days Waiting</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Due Date</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {approvalsData.pending_items.map((item) => (
                          <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <p className="font-medium text-slate-900">{item.content_title}</p>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-slate-600">{item.approver}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-sm font-medium ${item.days_waiting >= 5 ? 'text-red-600' : item.days_waiting >= 3 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {item.days_waiting}d
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm text-slate-600">{item.due_date || '-'}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {item.days_waiting >= 5 ? (
                                <Badge className="bg-red-100 text-red-700">Flagged</Badge>
                              ) : item.days_waiting >= 3 ? (
                                <Badge className="bg-amber-100 text-amber-700">Warning</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-700">Normal</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Approver Breakdown */}
              <Card className="mcc-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Approver Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Approver</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Total</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Pending</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Approved</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Rejected</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Pending %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {approvalsData.approver_breakdown.map((approver) => (
                          <tr key={approver.name} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <p className="font-medium text-slate-900">{approver.name}</p>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm font-medium text-slate-600">{approver.total}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-sm font-medium ${approver.pending > 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {approver.pending}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-sm font-medium text-emerald-600">{approver.approved}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-sm font-medium ${approver.rejected > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                                {approver.rejected}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-sm font-medium">{approver.pending_pct}%</span>
                                <Progress value={approver.pending_pct} className="w-12 h-2 [&>div]:bg-amber-500" />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Request KPIs Tab */}
        <TabsContent value="requests" className="space-y-6">
          {loading ? <ReportSkeleton /> : requestsKPIData && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard
                  icon={Target}
                  label="Total Requests"
                  value={requestsKPIData.kpis.total_requests}
                  bgColor="bg-blue-50"
                  iconColor="text-blue-600"
                />
                <KPICard
                  icon={CheckCircle}
                  label="Completion Rate"
                  value={`${requestsKPIData.kpis.completion_rate}%`}
                  bgColor="bg-green-50"
                  iconColor="text-green-600"
                />
                <KPICard
                  icon={AlertCircle}
                  label="SLA Compliance"
                  value={`${requestsKPIData.kpis.sla_compliance_rate}%`}
                  bgColor={requestsKPIData.kpis.sla_compliance_rate >= 80 ? "bg-green-50" : "bg-amber-50"}
                  iconColor={requestsKPIData.kpis.sla_compliance_rate >= 80 ? "text-green-600" : "text-amber-600"}
                />
                <KPICard
                  icon={Clock}
                  label="Avg Completion"
                  value={`${requestsKPIData.kpis.avg_completion_days} days`}
                  bgColor="bg-purple-50"
                  iconColor="text-purple-600"
                />
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="mcc-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{requestsKPIData.kpis.open}</p>
                    <p className="text-sm text-slate-500">Open Requests</p>
                  </CardContent>
                </Card>
                <Card className="mcc-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{requestsKPIData.kpis.completed}</p>
                    <p className="text-sm text-slate-500">Completed</p>
                  </CardContent>
                </Card>
                <Card className="mcc-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{requestsKPIData.kpis.avg_response_hours}h</p>
                    <p className="text-sm text-slate-500">Avg Response Time</p>
                  </CardContent>
                </Card>
                <Card className="mcc-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{requestsKPIData.kpis.sla_breached}</p>
                    <p className="text-sm text-slate-500">SLA Breached</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* By Status */}
                <Card className="mcc-card">
                  <CardHeader>
                    <CardTitle className="text-base">Requests by Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={requestsKPIData.charts.by_status}
                            dataKey="count"
                            nameKey="status"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ status, count }) => `${status}: ${count}`}
                          >
                            {requestsKPIData.charts.by_status.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* By Priority */}
                <Card className="mcc-card">
                  <CardHeader>
                    <CardTitle className="text-base">Requests by Priority</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={requestsKPIData.charts.by_priority}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="priority" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Submission Trend */}
              <Card className="mcc-card">
                <CardHeader>
                  <CardTitle className="text-base">Request Submission & Completion Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={requestsKPIData.charts.submission_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#3b82f6" name="Submissions" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Workload & Top Requesters */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Workload by Assignee */}
                <Card className="mcc-card">
                  <CardHeader>
                    <CardTitle className="text-base">Team Workload (Open Requests)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={requestsKPIData.charts.workload_distribution} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={100} />
                          <Tooltip />
                          <Bar dataKey="open_requests" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Requesters */}
                <Card className="mcc-card">
                  <CardHeader>
                    <CardTitle className="text-base">Top Requesters</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-64">
                      <div className="space-y-2">
                        {requestsKPIData.top_requesters.map((r, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="text-sm font-medium">{r.name}</span>
                            <Badge variant="outline">{r.count} requests</Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* By Request Type */}
              <Card className="mcc-card">
                <CardHeader>
                  <CardTitle className="text-base">Requests by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={requestsKPIData.charts.by_type}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
